import Api from "@/api";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as Companies from "@/models/companies";
import * as React from "react";

import { PrimaryButton, showErrorToast, showSuccessToast } from "turboui";

import { usePaths } from "@/routes/paths";
import { PageModule } from "@/routes/types";

export default { name: "CompanyTempoPage", loader, Page } as PageModule;

interface LoaderResult {
  company: Companies.Company;
}

async function loader(): Promise<LoaderResult> {
  return {
    company: await Companies.getCompany({}).then((d) => d.company!),
  };
}

// Keys mirror Operately.Companies.Tempo.defaults/0 (snake_case on purpose: the stored map
// and the seeders use these exact names). Values are "run every N ..." paces, never 0 --
// tempo paces work, it does not pause it (pausing is a guardrail's job).
const KNOBS: { key: string; label: string; unit: string; help: string }[] = [
  {
    key: "quiet_team_days",
    label: "Quiet-team pace (days)",
    unit: "days",
    help: "How often the quiet teams (Data & Analytics, Intelligence, Design, Support) run their daily grooming and daily OKR pass. 1 = every day. Growth teams (Sales, Marketing, Partnerships, Social Media, Paid Advertising, Customer Success) and build teams (Engineering, Product) always run daily -- they are how the company grows.",
  },
  {
    key: "data_insights_weeks",
    label: "Data insights pass (weeks)",
    unit: "weeks",
    help: "How often Data & Analytics mines the product data and publishes proprietary insights (the raw material for Marketing's data-led articles). 1 = weekly; 4 = roughly monthly while the data is still thin.",
  },
  {
    key: "intel_brief_weeks",
    label: "Market brief (weeks)",
    unit: "weeks",
    help: "How often Intelligence writes the decision-ready market and competitor brief. 1 = weekly; 4 = roughly monthly in the early quiet.",
  },
  {
    key: "doctrine_patch_weeks",
    label: "Doctrine patches (weeks)",
    unit: "weeks",
    help: "How often every team runs its doctrine-patch pass (filing durable lessons). Lessons accumulate slowly at low activity; 4 = roughly monthly early on.",
  },
];

// Stage presets mirror Operately.Companies.Tempo.stages/0. Picking a stage fills the fields;
// every field stays individually overridable before saving.
const STAGES: { key: string; label: string; description: string; values: Record<string, number> }[] = [
  {
    key: "getting_started",
    label: "Getting started",
    description: "Pre-revenue / first customers. Quiet teams breathe slowly; every growth motion stays at full speed.",
    values: { quiet_team_days: 3, data_insights_weeks: 4, intel_brief_weeks: 4, doctrine_patch_weeks: 4 },
  },
  {
    key: "growing",
    label: "Growing",
    description: "Real customers and real data. The quiet teams have work most days.",
    values: { quiet_team_days: 2, data_insights_weeks: 2, intel_brief_weeks: 2, doctrine_patch_weeks: 2 },
  },
  {
    key: "scaling",
    label: "Scaling",
    description: "Full tempo: every team runs its fastest standing cadence.",
    values: { quiet_team_days: 1, data_insights_weeks: 1, intel_brief_weeks: 1, doctrine_patch_weeks: 1 },
  },
];

const DEFAULT_STAGE = "getting_started";

interface TempoState {
  stage: string;
  values: Record<string, number>;
}

// The backend serializes tempo as a JSON string (snake_case keys). Merge whatever it
// returned over the default stage's values so the form always shows a full set.
function parseTempo(raw?: string | null): TempoState {
  let parsed: Record<string, unknown> = {};

  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }

  const fallback = STAGES.find((s) => s.key === DEFAULT_STAGE)!.values;
  const stage = typeof parsed["stage"] === "string" && STAGES.some((s) => s.key === parsed["stage"]) ? (parsed["stage"] as string) : DEFAULT_STAGE;

  const values = KNOBS.reduce<Record<string, number>>((acc, k) => {
    const value = parsed[k.key];
    acc[k.key] = typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : fallback[k.key]!;
    return acc;
  }, {});

  return { stage, values };
}

function Page() {
  const paths = usePaths();
  const { company } = Pages.useLoadedData<LoaderResult>();

  return (
    <Pages.Page title={["Operating Tempo", company.name!]} testId="company-tempo-page">
      <Paper.Root size="small">
        <Paper.NavigateBack to={paths.companyAdminPath()} title="Back to Company Admin" />

        <Paper.Body>
          <div className="mb-2 text-content-accent text-2xl font-bold">Operating Tempo</div>
          <p className="mb-6 text-sm text-content-dimmed">
            How fast your AI workforce's standing rhythm runs. Pick the stage that matches where the company is --
            it fills smart defaults -- then override any value. Saving re-tunes the recurring tasks in every team
            automatically within a few minutes. Growth work (sales, marketing, partnerships, social, ads, customer
            success) always runs at full speed; tempo only paces the quiet, supporting cadences.
          </p>

          <Form company={company} />
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Form({ company }: { company: Companies.Company }) {
  const [state, setState] = React.useState<TempoState>(parseTempo(company.tempo));
  const [saving, setSaving] = React.useState(false);

  const applyStage = (stageKey: string) => {
    const stage = STAGES.find((s) => s.key === stageKey);
    if (!stage) return;
    setState({ stage: stageKey, values: { ...stage.values } });
  };

  const updateValue = (key: string, value: number) => {
    setState((prev) => ({ ...prev, values: { ...prev.values, [key]: Math.min(60, Math.max(1, Math.floor(value))) } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Api.editCompanyTempo({ companyId: company.id, tempo: JSON.stringify({ stage: state.stage, ...state.values }) });
      showSuccessToast("Saved", "Operating tempo updated -- recurring tasks re-tune on the next scheduler pass");
    } catch {
      showErrorToast("Error", "Failed to save the operating tempo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div data-test-id="tempo-stage-picker">
        <label className="block text-sm font-medium text-content-base mb-2">Company stage</label>
        <div className="space-y-2">
          {STAGES.map((s) => (
            <label
              key={s.key}
              className="flex items-start gap-3 rounded-md border border-surface-outline bg-surface-base px-3 py-2 cursor-pointer"
              data-test-id={`tempo-stage-${s.key}`}
            >
              <input
                type="radio"
                name="tempo-stage"
                className="mt-1"
                checked={state.stage === s.key}
                onChange={() => applyStage(s.key)}
              />
              <span>
                <span className="block text-sm font-medium text-content-base">{s.label}</span>
                <span className="block text-xs text-content-dimmed">{s.description}</span>
              </span>
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-content-dimmed">
          Picking a stage fills the values below with its defaults. You can still adjust each one before saving.
        </p>
      </div>

      {KNOBS.map((k) => (
        <div key={k.key} data-test-id={`tempo-${k.key}`}>
          <label className="block text-sm font-medium text-content-base mb-1">{k.label}</label>
          <input
            type="number"
            min={1}
            max={60}
            step={1}
            value={state.values[k.key]}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) updateValue(k.key, n);
            }}
            className="w-full rounded-md border border-surface-outline bg-surface-base px-3 py-2"
            data-test-id={`tempo-input-${k.key}`}
          />
          <p className="mt-1 text-xs text-content-dimmed">{k.help}</p>
        </div>
      ))}

      <div className="flex justify-end">
        <PrimaryButton onClick={handleSave} loading={saving} testId="save-tempo">
          Save changes
        </PrimaryButton>
      </div>
    </div>
  );
}
